/**
 * Experiences Loading Placeholder Component
 * Displays skeleton loading placeholders for experience cards in grid layout
 * Matches the GridExperienceCard structure with different patterns for search vs initial load
 */

import React from 'react';
import {
  spacing,
  borders,
  backgroundColors,
  strokeColors,
  typography,
} from '@/design-system/utils';

export interface ExperiencesLoadingPlaceholderProps {
  /**
   * Pattern variant for different loading states
   * 'initial' - Initial load pattern (uniform cards)
   * 'search' - Search pattern (varied card sizes and content)
   * @default 'initial'
   */
  pattern?: 'initial' | 'search';
  /**
   * Number of skeleton cards to display
   * @default 6
   */
  count?: number;
}

export const ExperiencesLoadingPlaceholder: React.FC<
  ExperiencesLoadingPlaceholderProps
> = ({ pattern = 'initial', count = 6 }) => {
  // Generate skeleton cards with different patterns
  const generateSkeletonCards = () => {
    const cards = [];
    for (let i = 0; i < count; i++) {
      if (pattern === 'search') {
        // Search pattern: varied widths and content
        const widthVariations = ['100%', '95%', '90%', '85%'];
        const titleWidths = ['80%', '70%', '90%', '75%'];
        const descriptionLines = [1, 2, 1, 2];
        const hasTags = i % 2 === 0;

        cards.push({
          key: i,
          imageWidth: widthVariations[i % widthVariations.length],
          titleWidth: titleWidths[i % titleWidths.length],
          descriptionLines: descriptionLines[i % descriptionLines.length],
          hasTags,
          priceWidth: i % 3 === 0 ? '60%' : '80%',
        });
      } else {
        // Initial pattern: uniform cards
        cards.push({
          key: i,
          imageWidth: '100%',
          titleWidth: '85%',
          descriptionLines: 2,
          hasTags: true,
          priceWidth: '70%',
        });
      }
    }
    return cards;
  };

  const skeletonCards = generateSkeletonCards();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[4],
        width: '100%',
      }}
    >
      <div
        style={{
          width: '100%',
        }}
        className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6'
      >
        {skeletonCards.map(card => (
          <div
            key={card.key}
            style={{
              display: 'flex',
              backgroundColor: backgroundColors.primary,
              border: `1px solid ${strokeColors.light}`,
              borderRadius: borders.radius.xl,
              padding: spacing[2], // 8px
              flexDirection: 'column',
              gap: spacing[1.5], // 6px
              boxShadow: '0px 4px 12px 0px rgba(0,0,0,0.06)',
              boxSizing: 'border-box',
            }}
            className='w-full sm:min-w-[220px] sm:max-w-[220px] sm:w-[220px]'
          >
            {/* Image Container Skeleton - matches actual image container */}
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: '160px',
                borderRadius: borders.radius.lg,
                overflow: 'hidden',
                backgroundColor: '#E5E7EB', // Light gray for image placeholder
                backgroundImage:
                  'linear-gradient(135deg, #E5E7EB 0%, #D1D5DB 100%)', // Subtle gradient
              }}
            >
              {/* Badge Skeleton - matches actual badge structure */}
              <div
                style={{
                  position: 'absolute',
                  top: spacing[1],
                  left: spacing[1],
                  zIndex: 2,
                }}
              >
                <div
                  style={{
                    backgroundColor: '#fed7aa', // Default badge offset color
                    border: '1px solid #fdba74', // Default badge solid color
                    borderRadius: borders.radius.lg,
                    paddingLeft: spacing[1],
                    paddingRight: spacing[2],
                    paddingTop: spacing[1],
                    paddingBottom: spacing[1],
                    display: 'flex',
                    gap: spacing[1],
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {/* Icon container skeleton */}
                  <div
                    style={{
                      backgroundColor: '#fdba74',
                      borderRadius: borders.radius.md,
                      padding: spacing[0.5],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '12px',
                      height: '12px',
                    }}
                    className='animate-pulse'
                  />
                  {/* Badge text skeleton */}
                  <div
                    style={{
                      height: '10px',
                      width: '50px',
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      borderRadius: '2px',
                    }}
                    className='animate-pulse'
                  />
                </div>
              </div>

              {/* Progress dots skeleton (if multiple images) */}
              {pattern === 'search' && card.key % 3 === 0 && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: spacing[2],
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: '4px',
                    zIndex: 3,
                  }}
                >
                  {[1, 2, 3].map(dot => (
                    <div
                      key={dot}
                      style={{
                        width: '4px',
                        height: '4px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Content Container Skeleton */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing[1.5],
                paddingLeft: spacing[1],
                paddingRight: spacing[1],
                paddingTop: spacing[1.5],
                paddingBottom: spacing[1.5],
              }}
            >
              {/* Tags Skeleton - matches actual tags with bullet separators */}
              {card.hasTags && (
                <div
                  style={{
                    display: 'flex',
                    gap: spacing[1],
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    flexWrap: 'wrap',
                  }}
                >
                  {[1, 2, 3].map((tag, index) => (
                    <React.Fragment key={tag}>
                      <div
                        style={{
                          height: '14px',
                          width:
                            index === 0
                              ? '60px'
                              : index === 1
                                ? '50px'
                                : '55px',
                          backgroundColor: backgroundColors.shade,
                          borderRadius: '2px',
                        }}
                        className='animate-pulse'
                      />
                      {index < 2 && (
                        <span
                          style={{
                            color: backgroundColors.shade,
                            fontSize: typography.size.xs,
                            display: 'inline-block',
                            width: '4px',
                            textAlign: 'center',
                          }}
                        >
                          •
                        </span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}

              {/* Title and Location Skeleton - matches actual structure */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing[1],
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: spacing[1],
                  }}
                >
                  {/* Title skeleton - 18px font, 2 lines, minHeight 50.4px */}
                  <div
                    style={{
                      height: '18px',
                      width: card.titleWidth,
                      backgroundColor: backgroundColors.shade,
                      borderRadius: '4px',
                      marginBottom: '4px',
                    }}
                    className='animate-pulse'
                  />
                  {pattern === 'search' && card.key % 4 === 0 ? (
                    <div
                      style={{
                        height: '18px',
                        width: '60%',
                        backgroundColor: backgroundColors.shade,
                        borderRadius: '4px',
                      }}
                      className='animate-pulse'
                    />
                  ) : (
                    <div
                      style={{
                        height: '18px',
                        width: '70%',
                        backgroundColor: backgroundColors.shade,
                        borderRadius: '4px',
                      }}
                      className='animate-pulse'
                    />
                  )}

                  {/* Location skeleton - matches actual location icon and text */}
                  <div
                    style={{
                      display: 'flex',
                      gap: spacing[1],
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      marginTop: spacing[1],
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flex: '1 0 0',
                        alignItems: 'flex-end',
                        minWidth: 0,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          gap: spacing[1],
                          alignItems: 'center',
                          minWidth: 0,
                          overflow: 'hidden',
                          maxWidth: '100%',
                          width: '100%',
                        }}
                      >
                        {/* Location icon skeleton - matches SVG size */}
                        <div
                          style={{
                            width: '12px',
                            height: '12px',
                            backgroundColor: backgroundColors.shade,
                            borderRadius: '2px',
                            flexShrink: 0,
                          }}
                          className='animate-pulse'
                        />
                        {/* Location text skeleton */}
                        <div
                          style={{
                            height: '14px',
                            width: '100px',
                            backgroundColor: backgroundColors.shade,
                            borderRadius: '4px',
                            flex: '1 1 auto',
                          }}
                          className='animate-pulse'
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description Skeleton - always 2 lines, minHeight 33.6px */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing[1],
                  minHeight: '33.6px', // Matches actual description minHeight
                }}
              >
                <div
                  style={{
                    height: '12px',
                    width: '100%',
                    backgroundColor: backgroundColors.shade,
                    borderRadius: '4px',
                  }}
                  className='animate-pulse'
                />
                <div
                  style={{
                    height: '12px',
                    width: '85%',
                    backgroundColor: backgroundColors.shade,
                    borderRadius: '4px',
                  }}
                  className='animate-pulse'
                />
              </div>

              {/* Price Skeleton - matches actual price section with colored background */}
              <div
                style={{
                  backgroundColor: '#fff7ed', // Default price background color (activities)
                  borderRadius: borders.radius.lg,
                  padding: `${spacing[3]} 0`, // 12px vertical padding
                  display: 'flex',
                  gap: spacing[1],
                  alignItems: 'center',
                  justifyContent: 'center', // Center content per design
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  minWidth: 0,
                  overflow: 'hidden',
                  lineHeight: 'normal',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: spacing[0],
                    alignItems: 'end',
                    flexShrink: 0,
                  }}
                >
                  {/* Price amount skeleton */}
                  <div
                    style={{
                      height: '20px',
                      width: card.priceWidth,
                      backgroundColor: backgroundColors.shade,
                      borderRadius: '4px',
                    }}
                    className='animate-pulse'
                  />
                </div>
                {/* Price suffix skeleton */}
                <div
                  style={{
                    height: '14px',
                    width: '60px',
                    backgroundColor: backgroundColors.shade,
                    borderRadius: '4px',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                  className='animate-pulse'
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add CSS animation for pulse effect */}
      <style jsx>{`
        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
};
